import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import classNames from 'classnames';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { FaInfoCircle } from 'react-icons/fa';

import intlShape from '@/shared/utils/intlShape';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import { emptyGuid } from '@/shared/utils/guid';
import { MainTaskIcon, SubTaskIcon } from '@/shared/components/Icons';
import messages from '../hrmMessages';

const TOTAL_ROW_ID = '00000000-0000-0000-0000-000000000001';

const applySorting = (rows, sortKey, sortDesc, hideableColumns) => {
  if (!rows || rows.length === 0) return rows;

  const sign = sortDesc ? -1 : 1;
  let ordering = (a, b) => 0;

  if (sortKey === 'employee') {
    ordering = (a, b) => sign * a.employee.localeCompare(b.employee);
  } else if (sortKey === 'employeeRole') {
    ordering = (a, b) =>
      sign * a.employeeRole.localeCompare(b.employeeRole) || a.employee.localeCompare(b.employee);
  } else if (sortKey === 'total') {
    ordering = (a, b) => sign * (a.total - b.total) || a.employee.localeCompare(b.employee);
  } else if (sortKey === 'employeeRemainingCapacity') {
    ordering = (a, b) =>
      sign * (a.employeeRemainingCapacity - b.employeeRemainingCapacity) ||
      a.employee.localeCompare(b.employee);
  } else if (sortKey === 'employeeTotalCapacity') {
    ordering = (a, b) =>
      sign * (a.employeeTotalCapacity - b.employeeTotalCapacity) ||
      a.employee.localeCompare(b.employee);
  } else if (sortKey === 'employeeContractCapacity') {
    ordering = (a, b) =>
      sign * ((a.employeeWeeklyHoursContract || 0) - (b.employeeWeeklyHoursContract || 0)) ||
      a.employee.localeCompare(b.employee);
  } else if (sortKey === 'employeeWeeklyCapacity') {
    ordering = (a, b) =>
      sign * (a.employeeWeeklyHours - b.employeeWeeklyHours) ||
      a.employee.localeCompare(b.employee);
  } else if (sortKey === 'employeeAvailableCapacity') {
    ordering = (a, b) =>
      sign *
        (a.employeeAvailableHours -
          (a.totalScheduledMinutes || a.total) -
          (b.employeeAvailableHours - (b.totalScheduledMinutes || b.total))) ||
      a.employee.localeCompare(b.employee);
  } else if (sortKey !== '') {
    const column = hideableColumns.findIndex(col => col.id === sortKey);
    if (column >= 0) {
      ordering = (a, b) =>
        sign * ((a.counts[column] || 0) - (b.counts[column] || 0)) ||
        a.employee.localeCompare(b.employee);
    }
  }

  return [...rows].sort((a, b) =>
    a.id === TOTAL_ROW_ID ? 1 : b.id === TOTAL_ROW_ID ? -1 : ordering(a, b),
  );
};

const CapacityPlanningReportTable = ({
  data,
  hideableColumns,
  columns,
  filters,
  loading,
  capacityColumnStatus,
  intl,
}) => {
  const show = {
    fullWeeklyCapacity: capacityColumnStatus.fullWeeklyCapacity || false,
    weeklyCapacity: capacityColumnStatus.weeklyCapacity !== false,
    totalCapacity: capacityColumnStatus.totalCapacity || false,
    remainingCapacity: capacityColumnStatus.remainingCapacity !== false,
    availableCapacity: capacityColumnStatus.availableCapacity !== false,
  };
  const capacityColCount =
    Object.values(show).filter(Boolean).length;

  const [sortKey, setSortKey] = useState('');
  const [sortDesc, setSortDesc] = useState(false);

  const handleSort = key => {
    if (key === sortKey) {
      setSortDesc(prev => !prev);
    } else {
      setSortKey(key);
      setSortDesc(false);
    }
  };

  const sortedData = useMemo(
    () => applySorting(data, sortKey, sortDesc, hideableColumns),
    [data, sortKey, sortDesc, hideableColumns],
  );

  const visibleColumnCount = columns.length;
  const getTasksGroupLabel = () => {
    if (filters.groupByMonth) return intl.formatMessage(messages.CapacityMonthOverview);
    if (filters.displayMode === 1) return intl.formatMessage(messages.CapacityTasksCount);
    if (filters.displayMode === 2) return intl.formatMessage(messages.CapacityTasksRelations);
    return intl.formatMessage(messages.CapacityTasks);
  };
  const dynamicGroupLabel = getTasksGroupLabel();

  const hours = intl.formatMessage(messages.HoursAbbreviated);

  const renderCapacityValue = minutes => {
    if (!minutes || minutes === 0) return '-';
    const sign = minutes < 0 ? '-' : '';
    const absMinutes = Math.round(Math.abs(minutes));
    const hrs = Math.floor(absMinutes / 60);
    const mins = absMinutes % 60;
    return mins === 0 ? `${sign}${hrs}${hours}` : `${sign}${hrs}${hours}${String(mins).padStart(2, '0')}`;
  };

  const renderValue = x => {
    if (filters.displayMode === 1 || filters.displayMode === 2) {
      return x === 0 ? '-' : x;
    }
    return renderCapacityValue(x);
  };

  const capacityColSpan = capacityColCount;
  const firstCapacityCol = ['fullWeeklyCapacity', 'weeklyCapacity', 'totalCapacity', 'remainingCapacity', 'availableCapacity']
    .find(key => show[key]);

  const renderContractCapacity = row => {
    const contractValue = renderCapacityValue(row.employeeWeeklyHoursContract);
    if (contractValue === '-') return '-';
    if (row.productivityPercentage == null) return contractValue;
    return `${contractValue} (${Number(row.productivityPercentage)}%)`;
  };

  const renderSortableHeader = (title, key, style, extraClassName, taskType, tooltip) => {
    const isActive = key === sortKey;
    const sortIcon = isActive
      ? classNames('fa margin10R', {
          'fa-caret-down': sortDesc,
          'fa-caret-up': !sortDesc,
        })
      : null;

    return (
      <th className={classNames(extraClassName)} style={style} key={key}>
        <span className="clickable" onClick={() => handleSort(key)}>
          {sortIcon && <i className={sortIcon} />}
          {taskType === 'mainTask' && <MainTaskIcon />}
          {taskType === 'subTask' && <SubTaskIcon />}
          {title}
        </span>
        {tooltip && (
          <OverlayTrigger
            trigger={['hover', 'focus']}
            placement="bottom"
            rootClose
            overlay={<Popover id={`popover-${key}`}>{tooltip}</Popover>}
          >
            <span className="explanation-icon margin5L">
              <FaInfoCircle />
            </span>
          </OverlayTrigger>
        )}
      </th>
    );
  };

  const renderRow = row => {
    const header = row.employee
      ? row.employee
      : row.id !== emptyGuid
      ? intl.formatMessage(messages.Totals)
      : intl.formatMessage(messages.Unassigned);

    return (
      <tbody className="hover" key={'row_' + row.id}>
        <tr>
          <td className={!row.isActive ? 'removed-user' : ''}>{header}</td>
          <td>{row.employeeRole}</td>
          {show.fullWeeklyCapacity && (
            <td className={firstCapacityCol === 'fullWeeklyCapacity' ? 'border-group' : undefined}>
              {renderContractCapacity(row)}
            </td>
          )}
          {show.weeklyCapacity && (
            <td className={firstCapacityCol === 'weeklyCapacity' ? 'border-group' : undefined}>
              {renderCapacityValue(row.employeeWeeklyHours)}
            </td>
          )}
          {show.totalCapacity && (
            <td className={firstCapacityCol === 'totalCapacity' ? 'border-group' : undefined}>
              {renderCapacityValue(row.employeeTotalCapacity)}
            </td>
          )}
          {show.remainingCapacity && (
            <td className={firstCapacityCol === 'remainingCapacity' ? 'border-group' : undefined}>
              {renderCapacityValue(row.employeeRemainingCapacity)}
            </td>
          )}
          {show.availableCapacity && (
            <td className={firstCapacityCol === 'availableCapacity' ? 'border-group' : undefined}>
              {renderCapacityValue(
                row.employeeAvailableHours - (row.totalScheduledMinutes || row.total),
              )}
            </td>
          )}
          {columns.map((column, idx) => (
            <td
              key={row.id + '_' + column}
              className={idx === 0 ? 'border-group' : undefined}
            >
              <span>{renderValue(row.counts[column] || 0)}</span>
            </td>
          ))}
          <td className="border-group">{renderValue(row.total)}</td>
        </tr>
      </tbody>
    );
  };

  const noData = !loading && (!sortedData || sortedData.length === 0);
  const totalColCount = 2 + capacityColCount + visibleColumnCount + 1;

  return (
    <div>
      <table className="table table-report capacity-report-table margin50B scrollable-table">
        <thead>
          <tr>
            <th colSpan={2} className="text-center">
              <FormattedMessage {...messages.Employee} />
            </th>
            {capacityColSpan > 0 && (
              <th colSpan={capacityColSpan} className="text-center border-group">
                <FormattedMessage {...messages.CapacityGroupHeader} />
              </th>
            )}
            {visibleColumnCount > 0 && (
              <th colSpan={visibleColumnCount} className="text-center border-group">
                {dynamicGroupLabel}
              </th>
            )}
            <th className="text-center border-group">
              <FormattedMessage {...messages.Totals} />
            </th>
          </tr>
          <tr>
            {renderSortableHeader(intl.formatMessage(messages.Employee), 'employee', {
              width: '200px',
            })}
            {renderSortableHeader(intl.formatMessage(messages.EmployeeRole), 'employeeRole', {
              width: '200px',
            })}
            {show.fullWeeklyCapacity &&
              renderSortableHeader(
                intl.formatMessage(messages.EmployeeFullWeeklyCapacity),
                'employeeContractCapacity',
                { width: '200px' },
                firstCapacityCol === 'fullWeeklyCapacity' ? 'border-group' : undefined,
              )}
            {show.weeklyCapacity &&
              renderSortableHeader(
                intl.formatMessage(messages.EmployeeWeeklyCapacity),
                'employeeWeeklyCapacity',
                { width: '200px' },
                firstCapacityCol === 'weeklyCapacity' ? 'border-group' : undefined,
              )}
            {show.totalCapacity &&
              renderSortableHeader(
                intl.formatMessage(messages.EmployeeTotalCapacity),
                'employeeTotalCapacity',
                { width: '200px' },
                firstCapacityCol === 'totalCapacity' ? 'border-group' : undefined,
                undefined,
                intl.formatMessage(messages.EmployeeTotalCapacityTooltip),
              )}
            {show.remainingCapacity &&
              renderSortableHeader(
                intl.formatMessage(messages.EmployeeRemainingCapacity),
                'employeeRemainingCapacity',
                { width: '200px' },
                firstCapacityCol === 'remainingCapacity' ? 'border-group' : undefined,
                undefined,
                intl.formatMessage(messages.EmployeeRemainingCapacityTooltip),
              )}
            {show.availableCapacity &&
              renderSortableHeader(
                intl.formatMessage(messages.EmployeeAvailableCapacity),
                'employeeAvailableCapacity',
                { width: '200px' },
                firstCapacityCol === 'availableCapacity' ? 'border-group' : undefined,
                undefined,
                intl.formatMessage(messages.EmployeeAvailableCapacityTooltip),
              )}
            {hideableColumns &&
              hideableColumns.map((col, index) =>
                columns.includes(index)
                  ? renderSortableHeader(
                      col.id === emptyGuid ? intl.formatMessage(messages.WithoutTemplate) : col.label,
                      col.id,
                      { width: '200px' },
                      index === columns[0] ? 'border-group' : undefined,
                      filters.mainTaskTemplateId && !filters.groupByMonth
                        ? col.id === filters.mainTaskTemplateId
                          ? 'mainTask'
                          : 'subTask'
                        : undefined,
                    )
                  : null,
              )}
            {renderSortableHeader(
              intl.formatMessage(messages.Totals),
              'total',
              undefined,
              'border-group',
            )}
          </tr>
        </thead>
        {noData && (
          <tbody>
            <tr className="no-data-row">
              <td colSpan={totalColCount}>
                <FormattedMessage {...messages.NoEmployeesFound} />
              </td>
            </tr>
          </tbody>
        )}
        {!loading &&
          sortedData &&
          sortedData.length > 0 &&
          sortedData.map(item => renderRow(item))}
      </table>
      {loading && (
        <div>
          <LoadingAnimation />
        </div>
      )}
    </div>
  );
};

CapacityPlanningReportTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  hideableColumns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      selected: PropTypes.bool,
    }),
  ).isRequired,
  columns: PropTypes.arrayOf(PropTypes.number).isRequired,
  filters: PropTypes.shape({
    displayMode: PropTypes.number,
    mainTaskTemplateId: PropTypes.string,
    groupByMonth: PropTypes.bool,
  }).isRequired,
  loading: PropTypes.bool,
  capacityColumnStatus: PropTypes.object,
  intl: intlShape.isRequired,
};

CapacityPlanningReportTable.defaultProps = {
  loading: false,
  capacityColumnStatus: {},
};

export default injectIntl(CapacityPlanningReportTable);
